// The locator configuration's public pin layers (asset groups): their points are drawn as category markers next to
// the businesses, each layer is a category under "Map Layers" in the category panel, and the points are searchable.
export const PIN_LAYER_SEARCH_TYPE = "PIN_LAYER";
// a single point picked from the search menu
export const PIN_LAYER_POINT_SEARCH_TYPE = "PIN_LAYER_POINT";
export const PIN_LAYER_GROUP_NAME = "Map Layers";

const hexColor = color => `#${(color ?? "263239").replace("#", "")}`;

// the layer's default marker style
export const pinLayerStyle = pinLayer => ({
  color: hexColor(pinLayer.defaultConfiguration?.backgroundColor),
  icon: pinLayer.defaultConfiguration?.icon ?? "location-pin"
});

// a point's marker style: its custom configuration (point.icon is "<layerId>-<configurationId>"), else the default
const pointStyle = (pinLayer, point) => {
  const configuration = pinLayer.customConfigurations?.find(
    c => point.icon === `${pinLayer.id}-${c.id}`
  );
  return configuration
    ? {
        color: hexColor(configuration.backgroundColor),
        icon: configuration.icon ?? pinLayerStyle(pinLayer).icon
      }
    : pinLayerStyle(pinLayer);
};

// the search option a layer category selects (see LocatorContainer's selectedSearchOption)
export const pinLayerSearchOption = pinLayer => ({
  searchType: PIN_LAYER_SEARCH_TYPE,
  value: pinLayer.id,
  label: pinLayer.title,
  searchTerms: [pinLayer.title],
  icon: pinLayerStyle(pinLayer).icon,
  objectLabel: PIN_LAYER_GROUP_NAME
});

// the category panel group listing each layer
export const pinLayerCategoryGroup = pinLayers =>
  pinLayers?.length
    ? {
        name: PIN_LAYER_GROUP_NAME,
        categories: pinLayers.map(pinLayer => ({
          key: `pin-layer-${pinLayer.id}`,
          label: pinLayer.title,
          ...pinLayerStyle(pinLayer),
          option: pinLayerSearchOption(pinLayer)
        }))
      }
    : null;

// every point as the result item LocatorSearchResultsComponent lists, shaped like a business location
export const buildPinLayerResults = pinLayers =>
  (pinLayers ?? []).flatMap(pinLayer =>
    (pinLayer.points ?? []).map(point => {
      const address = point.location ?? point.nearestAddress;
      return {
        id: point.id,
        displayName: point.title,
        description: point.description,
        coverPhotoId: point.assets?.find(asset => asset.assetType === "IMAGE")?.id,
        streetAddress: address?.streetAddress,
        location: {
          ...address,
          latitude: point.latitude,
          longitude: point.longitude
        },
        pinLayerId: pinLayer.id,
        pinLayerTitle: pinLayer.title,
        pinLayer,
        pinLayerPoint: point,
        ...pointStyle(pinLayer, point)
      };
    })
  );

// results -> locator features; markerRef draws the icon pin (LOCATOR_ICONS in MapBoxMap)
export const buildPinLayerFeatures = results =>
  results.map(result => ({
    type: "Feature",
    geometry: {
      type: "Point",
      coordinates: [result.location.longitude, result.location.latitude]
    },
    properties: {
      id: result.id,
      pinLayerPoint: true,
      pinLayerId: result.pinLayerId,
      displayName: result.displayName,
      hexColor: result.color,
      markerRef: `${result.color.replace("#", "")}/${result.icon}`
    }
  }));

// the search menu options for each point
export const buildPinLayerPointSearchOptions = results =>
  results.map(result => ({
    id: result.id,
    value: result.id,
    label: result.displayName,
    searchTerms: [result.displayName],
    icon: result.icon,
    searchType: PIN_LAYER_POINT_SEARCH_TYPE,
    objectLabel: result.pinLayerTitle
  }));

// a text query's matching points (title or description)
export const matchPinLayerResults = (results, text) => {
  const query = text?.trim().toLowerCase();
  if (!query) return [];
  return results.filter(
    result =>
      result.displayName?.toLowerCase().includes(query) ||
      result.description?.toLowerCase().includes(query)
  );
};
